import { age } from "./body";
import { addDays, clockTime, dayLabel, DOW_LABELS, parseDay, todayStr, weekDays } from "./date";
import { stepBreakdown } from "./energy";
import { MEAL_PROTEIN_AIM_G } from "./foodTrends";
import { planFor, totalsOf } from "./intake";
import { sessionKeyFor, typeName } from "./plan";
// the test readers sit in programme.ts for now; one import here, so moving them is a one-line change
import { latestTests, rangeFor, retestDue } from "./fitnessTests";
import { programmeFor, walkDayName } from "./programme";
import { steadyFigures, steadyWeekRows } from "./programmeView";
import { dayFull } from "./stats";
import { activeMinutes, WHO_MINUTES } from "./training";
import { readBack, trainingScreens, type ScreenId, type TrainingDraftAnswers } from "./trainingQuestions";
import type { HowWas, KayaState, Programme } from "./types";
import { walkSecs } from "./walk";
import { clock12 } from "./water";

/*
 * What the family view says about a linked person (ADR-046; board FamilyView). Pure, over the
 * other person's state as the link reads it — the same functions their own phone runs, so the
 * owner sees the figures the parent sees.
 */

export interface FamilyWeek {
  /** Monday and Sunday of the week holding `today` */
  from: string;
  to: string;
  /** days of that week with anything planned */
  planned: number;
  /** planned dinners short of the per-meal protein aim; null when no dinner is planned */
  shortDinners: number | null;
}

/** The plan for the Monday-to-Sunday week that holds `today`, as the board's hero counts it. */
export function familyWeek(S: KayaState, today: string): FamilyWeek {
  const from = addDays(today, -((parseDay(today).getDay() + 6) % 7));
  let planned = 0;
  let dinners = 0;
  let short = 0;
  for (let i = 0; i < 7; i++) {
    const items = planFor(S, addDays(from, i));
    if (!items.length) continue;
    planned++;
    const dinner = items.filter((e) => e.meal === "dinner");
    if (!dinner.length) continue;
    dinners++;
    if (totalsOf(dinner).p < MEAL_PROTEIN_AIM_G) short++;
  }
  return { from, to: addDays(from, 6), planned, shortDinners: dinners ? short : null };
}

/** Today's steps, or null when nothing has counted any — "not measured" is not "did not move". */
export function familySteps(S: KayaState, ds: string): number | null {
  const b = stepBreakdown(S, ds);
  return b.basis === "none" ? null : b.total;
}

export interface Pronouns {
  /** "her plan" */
  their: string;
  /** "Her targets", at the start of a line */
  Their: string;
  /** "answer for her" */
  them: string;
}

/** The words for someone else's things, from the sex their profile names; "their" when it names none. */
export function pronouns(S: KayaState): Pronouns {
  const sex = S.profile.sex;
  if (sex === "female") return { their: "her", Their: "Her", them: "her" };
  if (sex === "male") return { their: "his", Their: "His", them: "him" };
  return { their: "their", Their: "Their", them: "them" };
}

/** "Mon 21 – Sun 27 Sep"; the month is said once when both ends share it. */
export function weekRangeLabel(from: string, to: string): string {
  const a = dayLabel(from);
  const b = dayLabel(to);
  const [aDow, aDay, aMon] = a.split(" ");
  return aMon === b.split(" ")[2] ? `${aDow} ${aDay} – ${b}` : `${a} – ${b}`;
}

/** What to call them:the reader's own word ("Mum"), else their profile's name, else the account's. */
export function familyName(label: string, S: KayaState | null, accountName: string): string {
  return label.trim() || S?.profile.name?.trim() || accountName.trim() || "Them";
}

/* ---------- training through the link (ADR-056; boards FamilyTraining, FamilyWeek) ---------- */

/** "How was it?" in words, as the parent's own phone says it: never a score. */
export const HOW_WAS_WORDS: Readonly<Record<HowWas["fit"], string>> = {
  easy: "Too easy",
  right: "About right",
  hard: "Too hard",
};

export interface FamilyLastSession {
  ds: string;
  /** "Strength & balance", "Joints + 2 walks" */
  name: string;
  /** minutes the logs hold for the day; null when no clock was kept */
  minutes: number | null;
  /** the day's "How was it?" in words; null when it was not answered */
  howWas: string | null;
}

export interface FamilyTraining {
  /** their programme; null before their exercise answers are saved */
  programme: Programme | null;
  /** Steady's strength days this Monday-to-Sunday week; null off the Steady programme */
  strength: { done: number; of: number } | null;
  /** walk minutes so far this week; null when nothing of theirs is logged this week */
  walkMin: number | null;
  /** their walks Monday to today: how many, their minutes, on how many days; null when nothing of
   *  theirs is logged this week — not measured is not "did not walk" (ADR-068) */
  walks: { count: number; min: number; days: number } | null;
  /** the latest chair-stand count, its day, the published line at their age, and who typed it */
  chairStand: { n: number; ds: string; line: number | null; by: string | null } | null;
  /** four weeks after the last tests; null before any */
  retest: string | null;
  /** the latest finished session in the last four weeks */
  last: FamilyLastSession | null;
  /** "Mon, Tue and Wed done · Thu not done · Fri today" */
  weekLine: string | null;
  /** who set their programme, when a linked owner did */
  setBy: string | null;
  /** the day their answers were last saved */
  answeredOn: string | null;
}

const LAST_SESSION_DAYS = 28;

function listWords(xs: string[]): string {
  return xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/** The week's days so far as plain words: what was done, what was not, and today. No red, no streak. */
export function familyWeekLine(S: KayaState, today: string): string | null {
  const rows = steadyWeekRows(S, today).filter((r) => !r.rest && r.ds <= today);
  const dow = (ds: string) => DOW_LABELS[parseDay(ds).getDay()];
  const done = rows.filter((r) => r.done).map((r) => dow(r.ds));
  const notDone = rows.filter((r) => !r.done && !r.isToday).map((r) => dow(r.ds));
  const todayRow = rows.find((r) => r.isToday && !r.done);
  const parts = [
    done.length ? `${listWords(done)} done` : null,
    notDone.length ? `${listWords(notDone)} not done` : null,
    todayRow ? `${dow(todayRow.ds)} today` : null,
  ].filter((x): x is string => !!x);
  return parts.length ? parts.join(" · ") : null;
}

function lastSession(S: KayaState, today: string): FamilyLastSession | null {
  for (let i = 0; i < LAST_SESSION_DAYS; i++) {
    const ds = addDays(today, -i);
    const L = S.logs[ds];
    if (!L || !dayFull(S, ds)) continue;
    const key = L.sessionKey || sessionKeyFor(S, ds);
    const min = activeMinutes(S, ds);
    return {
      ds,
      name: walkDayName(S, key) ?? typeName(key),
      minutes: min > 0 ? min : null,
      howWas: L.howWas ? (HOW_WAS_WORDS[L.howWas.fit] ?? null) : null,
    };
  }
  return null;
}

/** Their training as plain figures, over their state as the link reads it. */
export function familyTraining(S: KayaState, today: string): FamilyTraining {
  const T = S.profile.training;
  const at = typeof T?.at === "number" ? T.at : null;
  const programme = at !== null ? programmeFor(S, today) : null;
  const fig = programme === "steady" ? steadyFigures(S, today) : null;

  const days = weekDays(parseDay(today)).filter((ds) => ds <= today);
  const logged = days.some((ds) => !!S.logs[ds]);
  const walkMin = logged ? Math.round(days.reduce((a, ds) => a + walkSecs(S.logs[ds]?.walks), 0) / 60) : null;
  const walked = days.filter((ds) => (S.logs[ds]?.walks ?? []).length > 0);
  const walks = logged
    ? { count: walked.reduce((a, ds) => a + (S.logs[ds]?.walks ?? []).length, 0), min: walkMin ?? 0, days: walked.length }
    : null;

  const lt = latestTests(S, today);
  const a = age(S, parseDay(today));
  const chairStand =
    lt && typeof lt.tests.chairStand === "number"
      ? {
          n: lt.tests.chairStand,
          ds: lt.ds,
          line: rangeFor("chair", a, S.profile.sex)?.line ?? null,
          by: lt.tests.by ?? null,
        }
      : null;

  return {
    programme,
    strength: fig ? { done: fig.strengthDone, of: fig.strengthOf } : null,
    walkMin,
    walks,
    chairStand,
    retest: retestDue(S, today),
    last: lastSession(S, today),
    weekLine: at !== null ? familyWeekLine(S, today) : null,
    setBy: T?.setBy ?? null,
    answeredOn: at !== null ? todayStr(new Date(at)) : null,
  };
}

/* ---------- their walks (ADR-068; board FamilyWeek) ---------- */

export interface FamilyWalk {
  /** "7:10 am" */
  time: string;
  /** minutes walking, paused time left out; at least 1 for any walk that ran */
  min: number;
  /** the counter's steps for the walk; null when they were not measured — never 0 */
  steps: number | null;
}

/** A day's walks, earliest first: when, how long, and the steps when the counter measured them. */
export function familyWalks(S: KayaState, ds: string): FamilyWalk[] {
  return [...(S.logs[ds]?.walks ?? [])]
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))
    .map((w) => ({
      time: clock12(clockTime(w.startedAt)),
      min: w.secs > 0 ? Math.max(1, Math.round(w.secs / 60)) : 0,
      steps: typeof w.measuredSteps === "number" ? w.measuredSteps : null,
    }));
}

const grouped = (n: number) => Math.round(n).toLocaleString("en-US");

/** "Walks · 7:10 am 28 min · 5:30 pm 20 min, 2,900 steps"; null on a day with none. Figures, no
 *  pace word and no judgement. */
export function familyWalksLine(S: KayaState, ds: string): string | null {
  const ws = familyWalks(S, ds);
  if (!ws.length) return null;
  const one = (w: FamilyWalk) => `${w.time} ${w.min} min${w.steps !== null ? `, ${grouped(w.steps)} steps` : ""}`;
  return `${ws.length === 1 ? "Walk" : "Walks"} · ${ws.map(one).join(" · ")}`;
}

/** The day's extras the family board names, as words after the day's own: "yoga & breathing". */
export function familyExtrasLine(S: KayaState, ds: string): string | null {
  return (S.logs[ds]?.extras ?? []).includes("YB") ? "yoga & breathing" : null;
}

/** "walking 48 min of the 150 a week the WHO suggests" — plain figures, never "behind". */
export function familyWhoLine(f: FamilyTraining): string | null {
  return f.walks ? `walking ${f.walks.min} min of the ${WHO_MINUTES} a week the WHO suggests` : null;
}

/**
 * A read-back sentence turned to the person being read about: "You want" → "She wants", "your" →
 * "her". The read-back is written in the second person for the person's own phone; the owner reads
 * the same sentences about them, so the verbs it uses are conjugated here, and a test runs every
 * sentence it can produce through this and fails on a "you" left over.
 */
/** "she", "he" or "they": the subject the read-back's "you" becomes. */
export function subjectOf(p: Pronouns): "she" | "he" | "they" {
  return p.their === "her" ? "she" : p.their === "his" ? "he" : "they";
}

const PAST = new Set(["fell", "stood", "held", "answered", "said", "took"]);

export function aboutThem(s: string, p: Pronouns): string {
  const sub = subjectOf(p);
  const Sub = sub[0].toUpperCase() + sub.slice(1);
  const plural = sub === "they";
  const v = (verb: string): string => {
    // the plan line's past tenses (P9: the read-back while answering in their name) stay as they are
    if (plural || PAST.has(verb)) return verb;
    if (verb === "have") return "has";
    if (verb === "are") return "is";
    if (verb === "do") return "does";
    if (verb === "hurry") return "hurries";
    return `${verb}s`;
  };
  return s
    .replace(
      /\b(Y|y)ou (are|have|want|walk|exercise|stand|need|get|keep|fell|sleep|work|feel|change|stood|held|answered|said|took)\b/g,
      (_m, y: string, verb: string) => `${y === "Y" ? Sub : sub} ${v(verb)}`
    )
    .replace(/\b(and|sometimes|often) (feel|get|keep)\b/g, (_m, w: string, verb: string) => `${w} ${v(verb)}`)
    .replace(/\bwhat you do\b/g, `what ${sub} ${v("do")}`)
    .replace(/\bwhen you hurry\b/g, `when ${sub} ${v("hurry")}`)
    .replace(/\bYour\b/g, p.Their)
    .replace(/\byour\b/g, p.their)
    .replace(/\byou\b/g, p.them)
    // a screen's name is a name: "under Before you start" stays as the screen says it
    .replace(/\bBefore (her|him|them) start\b/g, "Before you start");
}

/**
 * The read-back as the owner sees it while he answers in their name (FamilyExercise, P9): the same
 * sentences, the plan line and Still right?'s rows turned to the third person. `p` null is the
 * person's own phone, unchanged.
 */
export function aboutWhom(p: Pronouns | null): (s: string) => string {
  return p ? (s) => aboutThem(s, p) : (s) => s;
}

export interface AnswerGroup {
  /** the questionnaire screen its Change opens */
  screen: ScreenId;
  text: string;
}

/**
 * Their exercise answers read back about them (board FamilyTrainingAnswers), one group per Change:
 * the read-back run over each group's answers alone, so every sentence knows the screen it came from.
 */
export function familyAnswerGroups(S: KayaState, p: Pronouns, today = new Date()): AnswerGroup[] {
  const T = S.profile.training;
  if (!T || typeof T.at !== "number") return [];
  const a: TrainingDraftAnswers = T;
  const screens = trainingScreens(S, a, today, { subject: true });
  const pick = (first: ScreenId, then: ScreenId): ScreenId => (screens.includes(first) ? first : then);
  const groups: [ScreenId, TrainingDraftAnswers][] = [
    ["goals", { goals: a.goals }],
    ["active", { active: a.active, days: a.days, minutes: a.minutes, when: a.when, mealWalks: a.mealWalks }],
    ["have", { have: a.have, joints: a.joints }],
    [
      pick("falls", "body"),
      { falls: a.falls, unsteady: a.unsteady, chairNoHands: a.chairNoHands, chairHelp: a.chairHelp, floor: a.floor },
    ],
    [pick("breathless", "style"), { breathless: a.breathless, style: a.style, voice: a.voice, lang: a.lang }],
  ];
  const out: AnswerGroup[] = [];
  // the chair question is asked with a joint, so the read-back needs the joints to word it; their
  // own sentences, which come first, are the joints group's and are dropped here
  const jointsOnly = readBack(S, { joints: a.joints }, today).length;
  for (const [i, [screen, part]] of groups.entries()) {
    const lines = i === 3 ? readBack(S, { ...part, joints: a.joints }, today).slice(jointsOnly) : readBack(S, part, today);
    const text = lines.map((s) => aboutThem(s, p)).join(" ");
    if (text) out.push({ screen, text });
  }
  return out;
}

/* ---------- the missed-week notice (ADR-056) ---------- */

/** The notice goes out on Sunday evening, India's time (ADR-045's clock), inside this window. */
export const MISSED_WEEK_FROM_MIN = 19 * 60;
export const MISSED_WEEK_UNTIL_MIN = 22 * 60;

export interface MissedWeekNotice {
  /** the week's Monday: the claim, so a week is told at most once */
  weekOf: string;
  title: string;
  body: string;
}

/** Sunday, in the evening window. */
export function missedWeekDue(ds: string, nowMin: number): boolean {
  return parseDay(ds).getDay() === 0 && nowMin >= MISSED_WEEK_FROM_MIN && nowMin < MISSED_WEEK_UNTIL_MIN;
}

/**
 * The owner's opt-in notice about a linked parent's week (plan §3.8; delivery-for-parents.md §5.2,
 * §6): a Steady week went by with no strength day done. The words are the count and nothing more —
 * "Mum: 0 of 3 this week" — because the son who reads it will call, and a judgement in it would
 * only reach the parent through him.
 *
 * Quiet unless `ds` is the week's Sunday; off the Steady programme; while the plan is held for a
 * physiotherapist; in the week the programme began (its days were not all theirs to do); when a
 * strength day was done or none was due; and for a week already told (`lastWeekOf`).
 */
export function missedWeekNotice(S: KayaState, ds: string, name: string, lastWeekOf?: string | null): MissedWeekNotice | null {
  if (parseDay(ds).getDay() !== 0) return null;
  const T = S.profile.training;
  if (!T || typeof T.at !== "number" || T.chairHelp) return null;
  if (programmeFor(S, ds) !== "steady") return null;
  const from = addDays(ds, -6);
  if (lastWeekOf === from) return null;
  if (todayStr(new Date(T.at)) > from) return null;
  const fig = steadyFigures(S, ds);
  if (fig.strengthOf === 0 || fig.strengthDone > 0) return null;
  return {
    weekOf: from,
    title: `${name}: 0 of ${fig.strengthOf} this week`,
    body: `No strength day was ticked in Kaya, ${weekRangeLabel(from, ds)}.`,
  };
}

/* ---------- what a link can do, in the words both ends read (ADR-056 amends ADR-046) ---------- */

/** The owner's side, on You › Family: what a link lets them do. */
export const LINK_READS =
  "A link reads their food, plan, targets, exercise week and their walks (when and how long), and lets you answer their eating and exercise questions. It never logs food or starts a session for them, and either of you can remove it at any time.";

/** The parent's side, on You › Account & data: what the person reading them sees and can change. */
export const LINKED_CAN =
  "They see your food, plan, targets, exercise week and your walks (when and how long), and can change your eating and exercise answers, test counts they timed with you, water settings and the Log dinner? reminder";
